/* Thin API client. The session token lives in localStorage, so members stay
   signed in between visits. */

// Always the direct Cloud Run address, which works from every host (CORS allows
// the .xyz origins). api.buildersnetwork.xyz is mapped too, but its certificate
// arrives later than the site's — so depending on it would break sign-in on the
// real domain until it does. Switching over buys nothing visible to members.
const BASE =
  process.env.REACT_APP_API_BASE || 'https://bn-api-200886230843.asia-southeast1.run.app';

const TOKEN_KEY = 'bn.token';
const ME_KEY = 'bn.me';
// Deliberately outlives clearSession(). Without it an expired token drops a
// member at a form that cannot tell them apart from a stranger, and they have
// no way of knowing that their own email brings their profile back.
const LAST_EMAIL_KEY = 'bn.lastEmail';

export function getToken() {
  return window.localStorage.getItem(TOKEN_KEY) || '';
}
export function getMe() {
  try {
    return JSON.parse(window.localStorage.getItem(ME_KEY) || 'null');
  } catch {
    return null;
  }
}
export function getLastEmail() {
  return window.localStorage.getItem(LAST_EMAIL_KEY) || '';
}
export function rememberEmail(email) {
  try {
    window.localStorage.setItem(LAST_EMAIL_KEY, email);
  } catch {
    /* private mode */
  }
}
export function setSession(payload) {
  window.localStorage.setItem(TOKEN_KEY, payload.access_token);
  window.localStorage.setItem(
    ME_KEY,
    JSON.stringify({ id: payload.member_id, handle: payload.handle, name: payload.name })
  );
}
export function replaceToken(token) {
  if (token) window.localStorage.setItem(TOKEN_KEY, token);
}
export function clearSession() {
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(ME_KEY);
}

async function request(path, { method = 'GET', body, auth = true } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (auth && token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 204) return null;

  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { detail: text };
  }

  // A token can outlive the member it points at (an account removed, a database
  // reset). Showing "account not found" on every screen is useless — drop the
  // dead session and send them back to the door with an explanation.
  if (res.status === 401 && auth) {
    clearSession();
    try {
      window.sessionStorage.setItem(
        'bn.notice',
        'You have been signed out — sessions expire after a while. Nothing is lost: '
        + 'Sign in again (Google or email) and your profile is exactly as you left it.'
      );
    } catch {
      /* private mode */
    }
    window.location.reload();
    return null;
  }

  if (!res.ok) {
    const detail = data?.detail;
    const message =
      typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.msg).join('; ')
          : `Request failed (${res.status})`;
    const error = new Error(message);
    error.status = res.status;
    throw error;
  }
  return data;
}

/* Multipart upload and authenticated download sit outside request(), which is
   JSON-only. Same error shape, so callers treat them alike. */
async function upload(path, formData) {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {},
    body: formData,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : `Upload failed (${res.status})`);
  return data;
}

/* A private file as an object URL, for showing it inline. Caller revokes it. */
async function blobUrl(path) {
  const res = await fetch(`${BASE}${path}`, {
    headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {},
  });
  if (!res.ok) throw new Error(`Couldn't load the image (${res.status})`);
  return URL.createObjectURL(new Blob([await res.blob()], { type: 'image/webp' }));
}

async function fileBlobUrl(path) {
  const res = await fetch(`${BASE}${path}`, {
    headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {},
  });
  if (!res.ok) throw new Error(`Couldn't load the image (${res.status})`);
  return URL.createObjectURL(await res.blob());
}

async function download(path, filename) {
  const res = await fetch(`${BASE}${path}`, {
    headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {},
  });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export const api = {
  base: BASE,
  health: () => request('/api/health', { auth: false }),
  places: () => request('/api/auth/places', { auth: false }),
  roster: () => request('/api/auth/roster'),
  invites: () => request('/api/auth/invites'),
  emailInvite: (code) => request(`/api/auth/invites/${code}/email`, { method: 'POST' }),
  createInvite: (payload = {}) =>
    request('/api/auth/invites', { method: 'POST', body: typeof payload === 'string' ? { email: payload } : payload }),
  publicStats: () => request('/api/public/stats', { auth: false }),
  requestInvite: (payload) => request('/api/public/invite-request', { method: 'POST', body: payload, auth: false }),
  adminInviteRequests: () => request('/api/admin/invite-requests'),
  decideInviteRequest: (id, decision) => request(`/api/admin/invite-requests/${id}/${decision}`, { method: 'POST' }),
  checkInvite: (code) =>
    request(`/api/auth/invites/${encodeURIComponent(code)}/check`, { auth: false }),
  googleSignIn: (credential, inviteCode = '') =>
    request('/api/auth/google', { method: 'POST', body: { credential, invite_code: inviteCode }, auth: false }),
  signup: (payload) => request('/api/auth/signup', { method: 'POST', body: payload, auth: false }),
  login: (email, password) =>
    request('/api/auth/login', { method: 'POST', body: { email, password }, auth: false }),
  changePassword: (currentPassword, newPassword) =>
    request('/api/auth/password', { method: 'POST', body: { current_password: currentPassword, new_password: newPassword } }),
  // payments
  myStorage: () => request('/api/payments/storage'),
  createOrder: (product) => request('/api/payments/orders', { method: 'POST', body: { product } }),
  checkOrder: (id) => request(`/api/payments/orders/${id}/check`, { method: 'POST' }),
  claimOrder: (id, txHash, network) =>
    request(`/api/payments/orders/${id}/claim`, { method: 'POST', body: { tx_hash: txHash, network } }),
  cancelOrder: (id) => request(`/api/payments/orders/${id}/cancel`, { method: 'POST' }),
  adminPayments: () => request('/api/admin/payments'),
  adminGrantStorage: (payload) => request('/api/admin/storage-grants', { method: 'POST', body: payload }),
  // mentions
  mentionCandidates: (postId) => request(`/api/mentions/candidates${postId ? `?post_id=${postId}` : ''}`),
  mentions: () => request('/api/mentions'),
  linkPreview: (url) => request(`/api/link-preview?url=${encodeURIComponent(url)}`),
  markMentionsRead: () => request('/api/mentions/read', { method: 'POST' }),
  removeMention: (id) => request(`/api/mentions/${id}`, { method: 'DELETE' }),
  // ads
  adServe: () => request('/api/ads/serve'),
  // Community: member count + online now, the Request button, free promotion standing.
  pulse: () => request('/api/community/pulse'),
  sendFeedback: (kind, text) => request('/api/community/feedback', { method: 'POST', body: { kind, text } }),
  promoteStatus: () => request('/api/community/promote'),
  adSeen: (id) => request(`/api/ads/${id}/seen`, { method: 'POST' }),
  adVisit: (id) => request(`/api/ads/${id}/visit`, { method: 'POST' }),
  adHide: (id, note = '') => request(`/api/ads/${id}/hide`, { method: 'POST', body: { note } }),
  adClaim: (id) => request(`/api/ads/${id}/claim`, { method: 'POST' }),
  adTalk: (id) => request(`/api/ads/${id}/talk`, { method: 'POST' }),
  adReport: (id, reason, note = '') => request(`/api/ads/${id}/report`, { method: 'POST', body: { reason, note } }),
  adFollowups: () => request('/api/ads/followups'),
  adOutcome: (id, payload) => request(`/api/ads/${id}/outcome`, { method: 'POST', body: payload }),
  perk: (slug) => request(`/api/perks/${encodeURIComponent(slug)}`),
  myAds: () => request('/api/ads/mine'),
  createAd: (payload) => request('/api/ads', { method: 'POST', body: payload }),
  editAd: (id, payload) => request(`/api/ads/${id}`, { method: 'PATCH', body: payload }),
  adManage: (id, action) => request(`/api/ads/${id}/manage/${action}`, { method: 'POST' }),
  adLogo: (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return upload(`/api/ads/${id}/logo`, form);
  },
  adStats: (id) => request(`/api/ads/${id}/stats`),
  adUploadCodes: (id, codes) => request(`/api/ads/${id}/codes`, { method: 'POST', body: { codes } }),
  adRedeem: (id, code) => request(`/api/ads/${id}/redeem`, { method: 'POST', body: { code } }),
  adConfirmOutcome: (id, outcomeId, confirmed) =>
    request(`/api/ads/${id}/outcomes/${outcomeId}`, { method: 'POST', body: { confirmed } }),
  adminAds: () => request('/api/admin/ads'),
  adminReviewAd: (id, decision, note = '') =>
    request(`/api/admin/ads/${id}/${decision}`, { method: 'POST', body: { note } }),
  block: (memberId) => request(`/api/safety/block/${memberId}`, { method: 'POST' }),
  unblock: (memberId) => request(`/api/safety/block/${memberId}`, { method: 'DELETE' }),
  blocked: () => request('/api/safety/blocked'),
  report: (payload) => request('/api/safety/report', { method: 'POST', body: payload }),
  acceptTerms: (version) => request('/api/legal/accept', { method: 'POST', body: { version } }),
  adminDashboard: () => request('/api/admin/dashboard'),
  adminNetwork: () => request('/api/admin/network'),
  network: () => request('/api/people/network'),
  dismissReport: (id) => request(`/api/admin/reports/${id}/dismiss`, { method: 'POST' }),
  closeReportedAccount: (id) => request(`/api/admin/reports/${id}/deactivate`, { method: 'POST' }),
  reviewAccounts: () => request('/api/admin/review-accounts'),
  setReviewAccount: (email, on) => request('/api/admin/review-accounts', { method: 'POST', body: { email, on } }),
  reactivateMember: (id) => request(`/api/admin/members/${id}/reactivate`, { method: 'POST' }),
  matchProfile: () => request('/api/matching/me'),
  saveMatchProfile: (data) => request('/api/matching/me', { method: 'PUT', body: data }),
  refreshMatchProfile: () => request('/api/matching/me/refresh', { method: 'POST' }),
  matchingOverview: () => request('/api/admin/matching'),
  setMatchingMode: (mode) => request('/api/admin/matching/mode', { method: 'POST', body: { mode } }),
  labelPair: (a, b, verdict, note = '') =>
    request('/api/admin/matching/label', { method: 'POST', body: { a, b, verdict, note } }),
  judgePair: (a, b) => request('/api/admin/matching/judge', { method: 'POST', body: { a, b } }),
  protectMember: (memberId, on) =>
    request('/api/admin/matching/protect', { method: 'POST', body: { member_id: memberId, protected: on } }),
  runMatching: () => request('/api/admin/matching/run', { method: 'POST' }),
  learnMatching: () => request('/api/admin/matching/learn', { method: 'POST' }),
  emailPrefs: () => request('/api/notify/prefs'),
  setEmailPrefs: (prefs) => request('/api/notify/prefs', { method: 'PATCH', body: prefs }),
  sendTestEmail: () => request('/api/notify/test', { method: 'POST' }),
  signOutEverywhere: () => request('/api/auth/sign-out-everywhere', { method: 'POST' }),
  tempPassword: (email) => request('/api/auth/temp-password', { method: 'POST', body: { email } }),
  enter: (payload) => request('/api/auth/enter', { method: 'POST', body: payload, auth: false }),
  me: () => request('/api/profiles/me'),
  updateMe: (payload) => request('/api/profiles/me', { method: 'PATCH', body: payload }),
  updatePrefs: (payload) => request('/api/profiles/me/prefs', { method: 'PATCH', body: payload }),
  uploadAvatar: (file) => {
    const form = new FormData();
    form.append('file', file);
    return upload('/api/profiles/me/avatar', form);
  },
  deleteAvatar: () => request('/api/profiles/me/avatar', { method: 'DELETE' }),
  updateIntent: (payload) => request('/api/profiles/me/intent', { method: 'PUT', body: payload }),
  confirmIntent: () => request('/api/profiles/me/intent/confirm', { method: 'POST' }),
  controls: () => request('/api/profiles/me/controls'),
  updateControls: (payload) =>
    request('/api/profiles/me/controls', { method: 'PATCH', body: payload }),
  addEvidence: (payload) =>
    request('/api/profiles/me/evidence', { method: 'POST', body: payload }),
  deleteEvidence: (id) => request(`/api/profiles/me/evidence/${id}`, { method: 'DELETE' }),
  profile: (handle) => request(`/api/profiles/${handle}`, { auth: Boolean(getToken()) }),

  files: () => request('/api/files'),
  uploadFile: (file, { purpose = 'other', visibility = 'private', useForMatching = true } = {}) => {
    const form = new FormData();
    form.append('file', file);
    form.append('purpose', purpose);
    form.append('visibility', visibility);
    form.append('use_for_matching', useForMatching ? 'true' : 'false');
    return upload('/api/files', form);
  },
  updateFile: (id, payload) => request(`/api/files/${id}`, { method: 'PATCH', body: payload }),
  deleteFile: (id) => request(`/api/files/${id}`, { method: 'DELETE' }),
  downloadFile: (id, filename) => download(`/api/files/${id}/download`, filename),
  imageUrl: (id) => blobUrl(`/api/files/${id}/download`),
  sharedFiles: (handle) => request(`/api/profiles/${handle}/files`),

  feed: (types = 'all', limit = 40, requestsOnly = false, view = 'all', before = '') =>
    request(`/api/feed?types=${types}&limit=${limit}${requestsOnly ? '&requests=true' : ''}`
      + `${view !== 'all' ? `&view=${view}` : ''}${before ? `&before=${encodeURIComponent(before)}` : ''}`),
  feedUpdates: () => request('/api/feed/updates'),
  savePost: (id) => request(`/api/posts/${id}/save`, { method: 'POST' }),
  unsavePost: (id) => request(`/api/posts/${id}/save`, { method: 'DELETE' }),
  find: (q, { types = '', fromId = '', kind = '', sinceDays = 0, mine = false } = {}) =>
    request(`/api/find?q=${encodeURIComponent(q)}${types ? `&types=${types}` : ''}${fromId ? `&from_id=${fromId}` : ''}`
      + `${kind ? `&kind=${kind}` : ''}${sinceDays ? `&since_days=${sinceDays}` : ''}${mine ? '&mine=true' : ''}`),
  myRequests: () => request('/api/requests/mine'),
  resolveRequest: (id, helpedBy) => request(`/api/posts/${id}/resolve`, { method: 'POST', body: { helped_by: helpedBy || null } }),
  requestHelpers: (id) => request(`/api/posts/${id}/helpers`),
  requestStats: () => request('/api/requests/stats'),
  reopenRequest: (id) => request(`/api/posts/${id}/reopen`, { method: 'POST' }),
  interests: () => request('/api/interests'),
  search: (q) => request(`/api/search?q=${encodeURIComponent(q)}`),
  createPost: (payload) => request('/api/posts', { method: 'POST', body: payload }),
  post: (id) => request(`/api/posts/${id}`),
  sharePost: (id, to, note) => request(`/api/posts/${id}/share`, { method: 'POST', body: { to, note } }),
  comments: (id, isNews) => request(`/api/${isNews ? 'news' : 'posts'}/${id}/comments`),
  editComment: (id, body) => request(`/api/comments/${id}`, { method: 'PATCH', body: { body } }),
  deleteComment: (id) => request(`/api/comments/${id}`, { method: 'DELETE' }),
  reactToComment: (id, emoji) => request(`/api/comments/${id}/react`, { method: 'POST', body: { emoji } }),
  reactToPost: (id, emoji) => request(`/api/posts/${id}/emoji`, { method: 'POST', body: { emoji } }),
  editPost: (id, payload) => request(`/api/posts/${id}`, { method: 'PATCH', body: payload }),
  deletePost: (id) => request(`/api/posts/${id}`, { method: 'DELETE' }),
  editMessage: (id, body) => request(`/api/messages/${id}`, { method: 'PATCH', body: { body } }),
  deleteMessage: (id) => request(`/api/messages/${id}`, { method: 'DELETE' }),
  reactToMessage: (id, emoji) => request(`/api/messages/${id}/react`, { method: 'POST', body: { emoji } }),
  groupEditMessage: (id, messageId, body) => request(`/api/groups/${id}/messages/${messageId}`, { method: 'PATCH', body: { body } }),
  comment: (id, body, isNews, replyToId) =>
    request(`/api/${isNews ? 'news' : 'posts'}/${id}/comments`, { method: 'POST', body: { body, reply_to_id: replyToId || null } }),
  react: (id, isNews) =>
    request(`/api/react?${isNews ? 'news_id' : 'post_id'}=${id}`, { method: 'POST' }),
  dismiss: (id, isNews, dimension) =>
    request('/api/dismiss', {
      method: 'POST',
      body: { [isNews ? 'news_id' : 'post_id']: id, dimension },
    }),
  ingestNews: () => request('/api/news/ingest', { method: 'POST' }),
  newsSources: () => request('/api/news/sources'),

  proposals: () => request('/api/proposals'),
  generateProposals: () => request('/api/proposals/generate', { method: 'POST' }),
  decideProposal: (id, decision, feedback = '') =>
    request(`/api/proposals/${id}/decide`, { method: 'POST', body: { decision, feedback } }),
  connections: () => request('/api/connections'),
  discover: (q) => request(`/api/people/discover${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  askToConnect: (memberId, note) => request(`/api/people/${memberId}/connect`, { method: 'POST', body: { note } }),
  cancelConnectRequest: (proposalId) => request(`/api/people/requests/${proposalId}`, { method: 'DELETE' }),
  sentRequests: () => request('/api/people/requests/sent'),
  refer: (postId, body) => request(`/api/posts/${postId}/referrals`, { method: 'POST', body }),
  postReferrals: (postId) => request(`/api/posts/${postId}/referrals`),
  myReferrals: () => request('/api/referrals/mine'),
  confirmReferral: (id) => request(`/api/referrals/${id}/confirm`, { method: 'POST' }),
  declineReferral: (id) => request(`/api/referrals/${id}/decline`, { method: 'POST' }),
  referralStatus: (id, status) => request(`/api/referrals/${id}/status`, { method: 'POST', body: { status } }),
  referralPaid: (id) => request(`/api/referrals/${id}/paid`, { method: 'POST' }),
  withdrawReferral: (id) => request(`/api/referrals/${id}/withdraw`, { method: 'POST' }),
  reviewSeen: (postId) => request(`/api/posts/${postId}/review-seen`, { method: 'POST' }),
  referralReceived: (id) => request(`/api/referrals/${id}/received`, { method: 'POST' }),
  pendingReferralPosts: () => request('/api/admin/referral-posts'),
  reviewReferralPost: (id, approve, reason = '') => request(`/api/admin/referral-posts/${id}/review`, { method: 'POST', body: { approve, reason } }),
  /* Working groups. */
  groups: () => request('/api/groups'),
  group: (id) => request(`/api/groups/${id}`),
  createGroup: (body) => request('/api/groups', { method: 'POST', body }),
  updateGroup: (id, body) => request(`/api/groups/${id}`, { method: 'PATCH', body }),
  groupInvite: (id, memberIds) => request(`/api/groups/${id}/invite`, { method: 'POST', body: { member_ids: memberIds } }),
  groupAccept: (id) => request(`/api/groups/${id}/accept`, { method: 'POST' }),
  groupDecline: (id) => request(`/api/groups/${id}/decline`, { method: 'POST' }),
  groupLeave: (id) => request(`/api/groups/${id}/leave`, { method: 'POST' }),
  groupRemove: (id, memberId) => request(`/api/groups/${id}/members/${memberId}`, { method: 'DELETE' }),
  groupAddTopic: (id, name) => request(`/api/groups/${id}/topics`, { method: 'POST', body: { name } }),
  groupCloseTopic: (id, topicId) => request(`/api/groups/${id}/topics/${topicId}`, { method: 'DELETE' }),
  groupMessages: (id, topicId, before) =>
    request(`/api/groups/${id}/topics/${topicId}/messages${before ? `?before=${before}` : ''}`),
  groupPost: (id, topicId, body, fileId, replyToId) =>
    request(`/api/groups/${id}/topics/${topicId}/messages`, { method: 'POST', body: { body, file_id: fileId || null, reply_to_id: replyToId || null } }),
  groupImageUrl: async (id, messageId) => {
    const res = await fetch(`${BASE}/api/groups/${id}/messages/${messageId}/image`, {
      headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {},
    });
    if (!res.ok) throw new Error(`Couldn't load the image (${res.status})`);
    return URL.createObjectURL(await res.blob());
  },
  groupReact: (id, messageId, emoji) => request(`/api/groups/${id}/messages/${messageId}/react`, { method: 'POST', body: { emoji } }),
  groupDeleteMessage: (id, messageId) => request(`/api/groups/${id}/messages/${messageId}`, { method: 'DELETE' }),
  groupPeople: (id) => request(`/api/groups/${id}/people`),
  groupPin: (id, body) => request(`/api/groups/${id}/pins`, { method: 'POST', body }),
  groupUnpin: (id, pinId) => request(`/api/groups/${id}/pins/${pinId}`, { method: 'DELETE' }),
  groupPinDownload: (id, pinId, filename) => download(`/api/groups/${id}/pins/${pinId}/download`, filename),
  sever: (id) => request(`/api/connections/${id}/sever`, { method: 'POST' }),
  threads: () => request('/api/threads'),
  unread: () => request('/api/unread'),
  thread: (id) => request(`/api/threads/${id}`),
  sendMessage: (id, body, replyToId, fileId) =>
    request(`/api/threads/${id}/messages`, { method: 'POST', body: { body, reply_to_id: replyToId || null, file_id: fileId || null } }),
  /* A file attached to a direct message (only the two of you can open it). */
  messageFileDownload: (messageId, filename) => download(`/api/messages/${messageId}/file`, filename),
  messageImageUrl: (messageId) => fileBlobUrl(`/api/messages/${messageId}/file?inline=true`),
  messageFileSave: (messageId) => request(`/api/messages/${messageId}/file/save`, { method: 'POST' }),
  /* A file attached to a group message (group members only). */
  groupFileDownload: (id, messageId, filename) => download(`/api/groups/${id}/messages/${messageId}/file`, filename),
  groupFileSave: (id, messageId) => request(`/api/groups/${id}/messages/${messageId}/file/save`, { method: 'POST' }),

  /* The assistant. A cold GPU takes ~20s to page a model into VRAM, so these
     calls can be slow in a way the rest of the API never is — callers must show
     that rather than looking hung. */
  presets: () => request('/api/assistant/presets'),
  assistantFeatures: () => request('/api/assistant/features'),
  conversations: () => request('/api/assistant/conversations'),
  conversation: (id) => request(`/api/assistant/conversations/${id}`),
  deleteConversation: (id) =>
    request(`/api/assistant/conversations/${id}`, { method: 'DELETE' }),
  ask: (body, preset, conversationId, image = false) =>
    request(
      `/api/assistant/messages${conversationId ? `?conversation_id=${conversationId}` : ''}`,
      { method: 'POST', body: { body, preset, image } }
    ),
  assistantUsage: () => request('/api/assistant/usage'),
  /* Talking about one of your own files. */
  attachFile: (fileId, conversationId) =>
    request('/api/assistant/conversations/attach', {
      method: 'POST', body: { file_id: fileId, conversation_id: conversationId || null },
    }),
  detachFile: (conversationId) =>
    request(`/api/assistant/conversations/${conversationId}/file`, { method: 'DELETE' }),
  runDocTool: (conversationId, tool) =>
    request(`/api/assistant/conversations/${conversationId}/tools/${tool}`, { method: 'POST' }),
  /* Applying sends only the action id and which wording to keep. The field being
     written lives on the server, so this cannot become a write-any-field call. */
  applyAction: (id, body) =>
    request(`/api/assistant/actions/${id}/apply`, { method: 'POST', body: body || {} }),
  dismissAction: (id) =>
    request(`/api/assistant/actions/${id}/dismiss`, { method: 'POST' }),
};
